/* eslint-disable indent */
/* eslint-disable @typescript-eslint/no-empty-function */
import Icon from '@able/react/dist/Icon';
import React, { PropsWithChildren, RefObject, createContext, useEffect, useId } from 'react';
import Popover from '../Popover/Popover';
import usePopover, { UsePopoverProps, UsePopoverReturnType } from '../Popover/usePopover';
import './style.scss';

export type DropdownMenuProps = {
    /** Show or hide menu panel */
    isExpanded: boolean;
    /** useToggleDropdown's setToggleDropdown function to change state of isExpanded */
    setExpanded: (value: boolean) => void;
    className?: string;
    developmentUrl?: string;
    /** DropdownMenu variant */
    variant?:
    | 'LowEmphasis'
    | 'LowEmphasisCompact'
    | 'MediumEmphasis'
    | 'MediumEmphasisCompact'
    | 'HighEmphasis'
    | 'HighEmphasisCompact'
    | 'EllipsisIcon'
    | 'EllipsisIconCompact';
    id?: string;
    /** Label shown on button */
    label: string;
    /** Object of event handlers
     */
    events?: Record<string, unknown>;
    contentAriaLabel?: string;
    /** Element ref to pass down targets button */
    buttonRef?: React.MutableRefObject<HTMLButtonElement | null>;
    /** Can pass function to run when Dropdown closes */
    onClose?: (button: HTMLButtonElement) => void;
    /** Can pass function to run when Dropdown opens */
    onOpen?: () => void;
} & Pick<UsePopoverProps,
    'preferredPosition'
    | 'autoPositionOnCollision'
    | 'trapFocus'
    | 'closeOnFocusOut'
    | 'hideOutOfView'> & PropsWithChildren;

export const DropdownMenuContext = createContext<Pick<UsePopoverReturnType<HTMLButtonElement>, 'focusAndClose'>>({
    focusAndClose: () => {}
});

const DropdownMenu = ({
    className = '',
    developmentUrl = '',
    variant = 'MediumEmphasisCompact',
    id,
    label,
    events,
    contentAriaLabel,
    children,
    // zIndex = 2,
    isExpanded,
    setExpanded,
    buttonRef,
    onOpen,
    onClose,
    preferredPosition,
    autoPositionOnCollision,
    trapFocus,
    closeOnFocusOut,
    hideOutOfView
}: DropdownMenuProps) => {

    const preFilledDropdownMenuId = useId();
    const DropdownClassName = `tedui-dropdown--${variant} ${className}`;
    const DropdownMenuId = id ? id : preFilledDropdownMenuId;
    const buttonId = `${DropdownMenuId}-button`;
    const contentId = `${DropdownMenuId}-content`;

    const { popoverRef, anchorRef, popoverProps, anchorProps, focusAndClose } = usePopover<HTMLButtonElement>({
        isOpen: isExpanded,
        setIsOpen: setExpanded,
        preferredPosition: preferredPosition,
        spacingType: 'none',
        trapFocus: trapFocus,
        enableVerticalNavigation: true,
        closeOnClickOutside: true,
        closeOnFocusOut,
        anchorId: buttonId,
        popoverId: contentId,
        popoverAriaLabel: contentAriaLabel,
        autoPositionOnCollision,
        hideOutOfView
    });

    const checkEllipsisIcon = (variant: string) => {
        if (variant === 'EllipsisIcon' || variant === 'EllipsisIconCompact') {
            return true;
        }
        return false;
    };

    const { onClick } = events || {};

    const handleClick = () => {
        // buttonClick.current = true;
        setExpanded(!isExpanded);
        if (onClick && typeof onClick === 'function') {
            onClick();
        }
    };

    useEffect(() => {
        if (isExpanded) onOpen && onOpen()
        else onClose && onClose(anchorRef.current as HTMLButtonElement)

        if (buttonRef)
            buttonRef.current = anchorRef.current as HTMLButtonElement;

    }, [isExpanded, anchorRef]);

    return (
        <div className={DropdownClassName} id={DropdownMenuId} >
            <button
                {...events}
                onClick={handleClick}
                ref={anchorRef as any}
                {...anchorProps as any}
            >
                {!checkEllipsisIcon(variant) ? label : ''}
                {!checkEllipsisIcon(variant) ? (
                    <Icon icon="ChevronDown" developmentUrl={developmentUrl} size="24" />
                ) : (
                    <Icon
                        icon="More"
                        developmentUrl={developmentUrl}
                        screenReaderContent={label}
                        size="24"
                    />
                )}
            </button>
            <DropdownMenuContext.Provider value={{ focusAndClose }}>
                {isExpanded && <Popover {...popoverProps} ref={popoverRef as RefObject<HTMLDivElement>}>
                    {children}
                </Popover>}
            </DropdownMenuContext.Provider>
        </div>
    );
};

export const DropdownMenuContent = DropdownMenuContext.Consumer;

export default DropdownMenu;
